import React, { useEffect, useMemo, useState } from "react";
import { Receipt, Upload, Download, Trash2, Loader2, CheckCircle2, XCircle, ChevronDown, Lock } from "lucide-react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import {
  subscribeF24Uploads,
  uploadF24,
  deleteF24,
} from "../../services/f24UploadsService";

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
const TYPE_LABELS = { acconto: "Acconto", saldo: "Saldo" };

function formatEuro(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

// Timestamp Firestore (o Date/stringa) -> Date, gestendo sia Timestamp che stringhe.
function toDate(ts) {
  if (!ts) return null;
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatUploadDate(ts) {
  const date = toDate(ts);
  if (!date) return "—";
  return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Chiave numerica di ordinamento: dueDate per gli upload utente, uploadedAt per
// quelli del commercialista (che non hanno una scadenza associata).
function sortValueOf(item) {
  if (item._kind === "user") {
    const d = new Date(item.dueDate);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  const d = toDate(item.uploadedAt);
  return d ? d.getTime() : 0;
}

// Decodifica una stringa base64 (senza prefisso data:) in un Blob e ne avvia il
// download: gli upload del commercialista sono salvati come base64 dentro
// Firestore (nessun fileUrl/Storage disponibile).
function downloadBase64File(base64, fileName) {
  try {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "f24.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Errore nella decodifica/download del F24:", err);
  }
}

// Sezione "F24 dal Commercialista" della pagina Commercialista: upload PDF F24
// da parte dell'utente (con scadenza/importo/tipo) + F24 caricati dal
// commercialista dal portale pubblico, uniti in un'unica lista raggruppata per anno.
export default function F24UploadsSection({ darkMode }) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [items, setItems] = useState([]);
  const [accountantItems, setAccountantItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openYears, setOpenYears] = useState(() => new Set());

  const [year, setYear] = useState(YEARS[0]);
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("acconto");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Upload utente (Storage + Firestore).
  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = subscribeF24Uploads(uid, (data) => {
      setItems(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [uid]);

  // Upload del commercialista (dal portale pubblico): salvati direttamente in
  // Firestore come base64, filtrati in memoria per type === "f24".
  useEffect(() => {
    if (!uid) {
      setAccountantItems([]);
      return undefined;
    }
    const q = query(collection(db, "users", uid, "accountantUploads"), orderBy("uploadedAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((item) => item.type === "f24");
        setAccountantItems(data);
      },
      (err) => {
        console.error("Errore lettura F24 del commercialista:", err);
        setAccountantItems([]);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  // Lista unificata (utente + commercialista). Gli upload del commercialista non
  // hanno un anno esplicito: si usa l'anno della data di caricamento come proxy
  // per poterli comunque raggruppare nell'accordion esistente.
  const mergedItems = useMemo(() => {
    const userItems = items.map((item) => ({ ...item, _kind: "user" }));
    const accItems = accountantItems.map((item) => {
      const uploadDate = toDate(item.uploadedAt);
      return {
        ...item,
        _kind: "accountant",
        year: uploadDate ? uploadDate.getFullYear() : 0,
      };
    });
    return [...userItems, ...accItems];
  }, [items, accountantItems]);

  const groupedByYear = useMemo(() => {
    const map = new Map();
    for (const item of mergedItems) {
      const y = item.year || 0;
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(item);
    }
    for (const list of map.values()) {
      list.sort((a, b) => sortValueOf(b) - sortValueOf(a));
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [mergedItems]);

  // Espande automaticamente l'anno più recente la prima volta che arrivano dati.
  useEffect(() => {
    if (groupedByYear.length > 0) {
      setOpenYears((prev) => (prev.size === 0 ? new Set([groupedByYear[0][0]]) : prev));
    }
  }, [groupedByYear]);

  const toggleYear = (y) => {
    setOpenYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y);
      else next.add(y);
      return next;
    });
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFormError(null);
    setFormSuccess(false);
    if (f && f.type !== "application/pdf") {
      setFormError("Seleziona un file PDF.");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uid) return;
    if (!dueDate) {
      setFormError("Inserisci la data di scadenza.");
      return;
    }
    if (!file) {
      setFormError("Seleziona un file PDF da caricare.");
      return;
    }
    setUploading(true);
    setFormError(null);
    setFormSuccess(false);
    try {
      await uploadF24(uid, { year, dueDate, amount, type, file });
      setFile(null);
      setDueDate("");
      setAmount("");
      const fileInput = document.getElementById("f24-file-input");
      if (fileInput) fileInput.value = "";
      setOpenYears((prev) => new Set(prev).add(year));
      setFormSuccess(true);
      setTimeout(() => setFormSuccess(false), 3000);
    } catch (err) {
      console.error("Errore caricamento F24:", err);
      setFormError(err?.message || "Errore durante il caricamento. Riprova.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!uid) return;
    setDeletingId(item.id);
    try {
      await deleteF24(uid, item.id, item.storagePath);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Errore eliminazione F24:", err);
      setFormError("Errore durante l'eliminazione. Riprova.");
    } finally {
      setDeletingId(null);
    }
  };

  const inputCls = darkMode
    ? "border-neutral-700 bg-neutral-800 text-neutral-200"
    : "border-neutral-200 bg-white text-neutral-800";

  return (
    <div>
      {/* Form upload */}
      <form onSubmit={handleUpload} className="flex flex-col gap-3 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1">
            <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>Anno</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className={`px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>Scadenza</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={`px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>Importo</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className={`px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>Tipo</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className={`px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            >
              <option value="acconto">Acconto</option>
              <option value="saldo">Saldo</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>File PDF</label>
            <input
              id="f24-file-input"
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className={`text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:cursor-pointer ${
                darkMode
                  ? "text-neutral-400 file:bg-neutral-800 file:text-neutral-300"
                  : "text-neutral-600 file:bg-neutral-100 file:text-neutral-700"
              }`}
            />
          </div>

          <button
            type="submit"
            disabled={uploading || !file}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50 shrink-0"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Carica
          </button>
        </div>
      </form>

      {formError && (
        <div className="mb-4 flex items-start gap-2 text-xs text-red-500">
          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {formError}
        </div>
      )}
      {formSuccess && (
        <div className="mb-4 flex items-start gap-2 text-xs text-green-600">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> F24 caricato con successo.
        </div>
      )}

      {/* Lista raggruppata per anno */}
      {loading ? (
        <div className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>Caricamento...</div>
      ) : groupedByYear.length === 0 ? (
        <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
          Nessun F24 caricato.
        </p>
      ) : (
        <div className="space-y-2">
          {groupedByYear.map(([y, list]) => {
            const isOpen = openYears.has(y);
            return (
              <div
                key={y}
                className={`rounded-lg border overflow-hidden ${
                  darkMode ? "border-neutral-800" : "border-neutral-100"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleYear(y)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium ${
                    darkMode
                      ? "bg-neutral-950/40 text-white hover:bg-neutral-900"
                      : "bg-neutral-50 text-neutral-900 hover:bg-neutral-100"
                  }`}
                >
                  <span>
                    {y || "Anno non specificato"}{" "}
                    <span className={`font-normal text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      ({list.length})
                    </span>
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <ul className={`divide-y ${darkMode ? "divide-neutral-800" : "divide-neutral-100"}`}>
                    {list.map((item) => {
                      const isUser = item._kind === "user";
                      return (
                        <li key={`${item._kind}-${item.id}`} className="flex items-center justify-between gap-3 p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Receipt className={`w-4 h-4 shrink-0 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                                  {isUser ? `${TYPE_LABELS[item.type] || item.type} · ${formatEuro(item.amount)}` : "F24"}
                                </span>
                                {isUser ? (
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      darkMode ? "bg-neutral-700 text-neutral-300" : "bg-neutral-200 text-neutral-700"
                                    }`}
                                  >
                                    Tu
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500 text-white">
                                    Commercialista
                                  </span>
                                )}
                              </div>
                              <div className={`text-xs truncate ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                                {isUser
                                  ? `Scadenza ${formatDateIT(item.dueDate)} · ${item.fileName}`
                                  : `${item.fileName} · caricato il ${formatUploadDate(item.uploadedAt)}`}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {isUser ? (
                              <a
                                href={item.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Scarica F24 ${TYPE_LABELS[item.type] || item.type} ${y}`}
                                className={`p-2 rounded-lg ${
                                  darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-500 hover:bg-neutral-100"
                                }`}
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            ) : (
                              <button
                                type="button"
                                onClick={() => downloadBase64File(item.fileDataBase64, item.fileName)}
                                aria-label={`Scarica F24 caricato dal commercialista${item.fileName ? `: ${item.fileName}` : ""}`}
                                className={`p-2 rounded-lg ${
                                  darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-500 hover:bg-neutral-100"
                                }`}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}

                            {!isUser ? (
                              <span
                                title="Caricato dal commercialista"
                                aria-label="Non eliminabile: caricato dal commercialista"
                                className={`p-2 rounded-lg opacity-40 cursor-not-allowed ${
                                  darkMode ? "text-neutral-500" : "text-neutral-400"
                                }`}
                              >
                                <Lock className="w-4 h-4" />
                              </span>
                            ) : confirmDeleteId === item.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item)}
                                  disabled={deletingId === item.id}
                                  className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                                >
                                  Conferma
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(null)}
                                  className={`px-2 py-1 rounded-lg text-xs ${
                                    darkMode
                                      ? "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
                                      : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
                                  }`}
                                >
                                  Annulla
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(item.id)}
                                aria-label={`Elimina F24 ${TYPE_LABELS[item.type] || item.type} ${y}`}
                                className={`p-2 rounded-lg ${
                                  darkMode ? "text-red-400 hover:bg-red-900/20" : "text-red-500 hover:bg-red-50"
                                }`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
